import { ResponsiveBar } from "@nivo/bar"
import {
	DataTableCellAlphaNumeric,
	DataTableCellNumeric,
	DataTableRow,
} from "@walmart-web/livingdesign-components"
import ReactTooltip from "react-tooltip"
import { formatNumber, formatPercentage } from "helpers/numbers"
import React from "react"
import { useIntl } from "react-intl"
import "./index.scss"
import { GenerateRowsTableProps } from "../../interfaces"
import {
	benchmarkLegends,
	CONFIG_MARGIN_RESPONSIVE_BAR,
	FIRST_BAR_SALES,
	INNER_PADDING_RESPONSIVE_BAR,
	KEYS_RESPONSIVE_BAR,
	PADDING_RESPONSIVE_BAR,
	textNoSales,
} from "../../constants"
import {
	barColors,
	generateIdTooltip,
	getBarColor,
	getNameToPeriodLegend,
	getSales,
	renderOperatorAndClassName,
	validateIsPreviousPeriod,
	validateLocationFilters,
} from "../../utils/utils"
import TooltipTable from "../TooltipTable"
import InformationBox from "../InformationBox"
import { titleCapitalize } from "helpers/text"

import { toPercentage } from "helpers/numbers"
import { right } from "@popperjs/core"

const GenerateRowsTable: React.FC<GenerateRowsTableProps> = ({
	data,
	maxValue,
	period,
	isPenetrationMetric = false,
	filtersBenchmark,
	metricName,
}): JSX.Element => {
	const intl = useIntl()

	const renderDataTableCell = (intlId: string) => {
		return (
			<DataTableCellAlphaNumeric UNSAFE_className="cell-alpha-numeric">
				<div>{intlId}</div>
			</DataTableCellAlphaNumeric>
		)
	}

	const renderNumericTableCell = (variation: number, value?: number) => {
		const valuesVariation = renderOperatorAndClassName(variation)
		return (
			<DataTableCellNumeric UNSAFE_className="cell-numeric">
				<div className="growth-cell">
					{variation === null ? (
						<>-</>
					) : (
						<p className={valuesVariation?.classCss}>
							{valuesVariation?.operator}
							{formatPercentage(variation, 1, true)}
						</p>
					)}
					{value === 0 ? (
						<p>(0)</p>
					) : (
						renderNumericValue(value, valuesVariation?.operator)
					)}
				</div>
			</DataTableCellNumeric>
		)
	}

	const renderNumericValue = (value?: number, operator?: string) => {
		return <p>{value && `(${operator}${formatNumber(value, intl)})`}</p>
	}

	const renderContentTooltip = (sales: any) => {
		const previousSales = sales[FIRST_BAR_SALES].Previous_Sales
		const currentSales = sales[FIRST_BAR_SALES].Current_Sales

		return (
			<TooltipTable className="tooltip-bar">
				{benchmarkLegends
					.map((legend, index) => (
						<InformationBox
							key={`tooltip-${index}`}
							backgroundColor={getBarColor(legend.period)}
						>
							<span>
								{intl.formatMessage({
									id: validateIsPreviousPeriod(legend.period)
										? getNameToPeriodLegend(period)?.previous
										: getNameToPeriodLegend(period).current,
								})}
								:
								<b>
									{formatNumber(
										validateIsPreviousPeriod(legend.period)
											? previousSales
											: currentSales,
										intl
									)}
								</b>
							</span>
						</InformationBox>
					))
					.reverse()}
			</TooltipTable>
		)
	}

	const renderTooltip = (sales: any, id: string) => {
		return (
			<ReactTooltip
				className="tooltip-table-benchmark"
				id={id}
				overridePosition={({ left, top }) => ({
					top: top + 20,
					left: left - 10,
				})}
				backgroundColor="transparent"
				getContent={() => renderContentTooltip(sales)}
			/>
		)
	}

	const cellPercentage = (percentage: number) => {
		const intl = useIntl()
		return (
			<DataTableCellAlphaNumeric UNSAFE_style={{ textAlign: right }}>
				<div>
					{toPercentage({
						value: percentage,
						intl: intl,
						withPercentageChar: true,
					})}
				</div>
			</DataTableCellAlphaNumeric>
		)
	}

	const penetrationCellRow = (
		currentNumber: number,
		previousNumber: number
	) => {
		return (
			<>
				{cellPercentage(previousNumber)}
				{cellPercentage(currentNumber)}
			</>
		)
	}

	const penetrationBar = (
		currentNumber: number,
		previousNumber: number,
		max: number,
		index: number
	) => {
		const units = [
			{
				Previous_Sales: currentNumber,
				Current_Sales: previousNumber,
			},
		]

		return (
			<DataTableCellAlphaNumeric
				data-tip
				data-for={generateIdTooltip(index)}
				UNSAFE_className="cell-alpha-numeric"
			>
				{renderTooltip(units, generateIdTooltip(index))}
				<div className="bar-chart-content">
					<ResponsiveBar
						innerPadding={INNER_PADDING_RESPONSIVE_BAR}
						padding={PADDING_RESPONSIVE_BAR}
						data={units}
						keys={KEYS_RESPONSIVE_BAR}
						indexBy="id"
						groupMode="grouped"
						layout="horizontal"
						maxValue={max}
						margin={CONFIG_MARGIN_RESPONSIVE_BAR}
						colors={[barColors.current, barColors.previous]}
						isInteractive={false}
						enableLabel={false}
					/>
				</div>
			</DataTableCellAlphaNumeric>
		)
	}

	const renderCustomCell = (
		currentNumber: number,
		previousNumber: number,
		index: number,
		max: number,
		isPenetrationMetric: boolean
	) => {
		return isPenetrationMetric
			? penetrationCellRow(currentNumber, previousNumber)
			: penetrationBar(currentNumber, previousNumber, max, index)
	}

	const renderPenetrationTableCell = (value?: number) => {
		return (
			<DataTableCellNumeric UNSAFE_className="cell-numeric">
				<div>
					{value === 0 || value === undefined ? (
						<p>0 pts</p>
					) : (
						renderPointsValue(value)
					)}
				</div>
			</DataTableCellNumeric>
		)
	}

	const renderPointsValue = (value: number) => {
		const valuesVariation = renderOperatorAndClassName(value)
		return (
			<p className={valuesVariation?.classCss}>
				{value && `${formatNumber(value, intl)} pts`}
			</p>
		)
	}

	const renderGrowth = (variation: number, value?: number) => {
		return isPenetrationMetric
			? renderPenetrationTableCell(value)
			: renderNumericTableCell(variation, value)
	}

	const mustShownStoreCode = (id: string) => {
		return validateLocationFilters(filtersBenchmark, metricName)
			? renderDataTableCell(id)
			: undefined
	}

	const getTableRowsWithoutBarcode = (row: any, index: number) => {
		return (
			<DataTableRow key={row.id}>
				{renderDataTableCell(titleCapitalize(row.name))}
				{mustShownStoreCode(row.id)}
				{getSales(row) > 0
					? renderCustomCell(
							row.previousSales,
							row.currentSales,
							index,
							maxValue,
							isPenetrationMetric
					  )
					: renderDataTableCell(intl.formatMessage(textNoSales))}
				{renderGrowth(row.growthPercentage, row.growthValue)}
				{isPenetrationMetric ||
					renderNumericTableCell(row.levelGrowthPercentage)}
			</DataTableRow>
		)
	}

	const getTableRowsWithBarcode = (row: any, index: number) => {
		return (
			<DataTableRow key={row.id}>
				{renderDataTableCell(titleCapitalize(row.name))}
				{renderDataTableCell(row.barcode)}
				{getSales(row) > 0
					? renderCustomCell(
							row.previousSales,
							row.currentSales,
							index,
							maxValue,
							isPenetrationMetric
					  )
					: renderDataTableCell(intl.formatMessage(textNoSales))}
				{renderGrowth(row.growthPercentage, row.growthValue)}
				{isPenetrationMetric ||
					renderNumericTableCell(row.levelGrowthPercentage)}
			</DataTableRow>
		)
	}

	const buildDataTableRow = (row: any, index: number) => {
		if (row.barcode === "0") {
			return getTableRowsWithoutBarcode(row, index)
		}
		return getTableRowsWithBarcode(row, index)
	}

	const renderTableRows = (segment: any) => {
		return segment.map((row: any, index: number) => {
			return buildDataTableRow(row, index)
		})
	}

	return <>{renderTableRows(data)}</>
}

export default GenerateRowsTable
